// Recordings — door knocks (GET /operations/door-knock-events/?limit=100) that
// carry a voice note, with the transcription state and the training flag.
import React, { useEffect, useRef, useState } from 'react';
import api from '../../api/client';
import { getUserRole } from '../../helpers/tenantHelpers';
import Button from '../../components/ui/button';
import Badge from '../../components/ui/badge';
import { DataTable } from '../../components/ui/table';
import RevivalHeader from '../../components/Revival/RevivalHeader';
import { count, sentence, shortDate } from './salesFormat';
import '../../components/Revival/revival.css';
import './sales-ds.css';

const TRANSCRIPTION_TONE = { pending: 'warning', processing: 'info', complete: 'success', failed: 'danger' };
const OUTCOME_TONE = { sold: 'success', interested: 'success', not_interested: 'danger', callback: 'info', not_home: 'neutral' };
const MANAGER = ['admin', 'owner', 'manager', 'ceo'];

function RecordingPlayer({ url }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play().catch(() => setError(true)); setPlaying(true); }
  };

  if (!url) return <span className="sl-muted">No recording</span>;
  if (error) return <span className="sl-muted">Playback unavailable</span>;
  return (
    <>
      <audio ref={audioRef} src={url} onEnded={() => setPlaying(false)} onError={() => setError(true)} preload="none" />
      <Button variant="secondary" size="sm" onClick={toggle}>{playing ? 'Pause' : 'Play'}</Button>
    </>
  );
}

export default function ConversationRecordings() {
  const isManager = MANAGER.includes(String(getUserRole() || '').toLowerCase());
  const [knocks, setKnocks] = useState([]);
  const [state, setState] = useState('loading');

  useEffect(() => {
    let alive = true;
    api.get('/operations/door-knock-events/', { params: { limit: 100 } })
      .then((res) => { if (alive) { setKnocks(res.data?.results || []); setState('ready'); } })
      .catch(() => { if (alive) setState('error'); });
    return () => { alive = false; };
  }, []);

  const withRecording = knocks.filter((k) => k.voice_note_url);

  const columns = [
    { key: 'created_at', label: 'Date', render: (k) => shortDate(k.created_at) },
    { key: 'rep_name', label: 'Rep', render: (k) => k.rep_name || (k.rep_id ? `Rep #${k.rep_id}` : '—') },
    { key: 'address', label: 'Address', render: (k) => <span className="sl-clip" title={k.address || ''}>{k.address || '—'}</span> },
    { key: 'outcome', label: 'Outcome', render: (k) => (k.outcome ? <Badge tone={OUTCOME_TONE[k.outcome] || 'neutral'}>{sentence(k.outcome)}</Badge> : '—') },
    { key: 'voice_note_url', label: 'Recording', sort: false, render: (k) => <RecordingPlayer url={k.voice_note_url} /> },
    {
      key: 'transcription_status', label: 'Transcription', render: (k) => (
        <>
          {k.transcription_status ? <Badge tone={TRANSCRIPTION_TONE[k.transcription_status] || 'neutral'}>{sentence(k.transcription_status)}</Badge> : '—'}
          {k.transcript_summary ? <div className="sl-muted sl-wrap">{k.transcript_summary}</div> : null}
        </>
      ),
    },
    ...(isManager ? [{ key: 'training_flag', label: 'Training', render: (k) => (k.training_flag ? <Badge tone="warning">Flagged</Badge> : '—') }] : []),
  ];

  return (
    <div className="rv-screen">
      <RevivalHeader
        title="Recordings"
        totals={state === 'ready' ? [<><b>{count(withRecording.length)}</b> recorded</>, <>{count(knocks.length)} knocks</>] : []}
      />
      {state === 'loading' ? <div className="rv-loading">Loading recordings…</div> : null}
      {state === 'error' ? <p className="sl-notice is-bad">Recordings didn’t load. Reload the page to try again.</p> : null}
      {state === 'ready' ? (
        <DataTable
          columns={columns}
          rows={withRecording}
          initialSort={{ key: 'created_at', dir: 'desc' }}
          empty="No recorded conversations yet. Reps record them during door knocks in the mobile app."
        />
      ) : null}
    </div>
  );
}
